import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { EpicomLogo } from "./brand/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_NAV_ITEMS = [
  { label: "Services", href: "#services", id: "nav-services" },
  { label: "Projects", href: "#projects", id: "nav-projects" },
  { label: "Coverage", href: "#coverage", id: "nav-coverage" },
  { label: "About", href: "#about", id: "nav-about" },
  { label: "Contact", href: "#contact", id: "nav-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCareers, setShowCareers] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Show "Careers" nav link only when the section is enabled AND has jobs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, j] = await Promise.all([
          axios.get(`${API}/settings/careers`),
          axios.get(`${API}/jobs`),
        ]);
        if (cancelled) return;
        const enabled = !!s.data?.enabled;
        const hasJobs = Array.isArray(j.data) && j.data.length > 0;
        setShowCareers(enabled && hasJobs);
      } catch {
        if (!cancelled) setShowCareers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Insert Careers before Contact when it should show
  const navItems = showCareers
    ? [
        ...BASE_NAV_ITEMS.slice(0, 4),
        { label: "Careers", href: "#careers", id: "nav-careers" },
        ...BASE_NAV_ITEMS.slice(4),
      ]
    : BASE_NAV_ITEMS;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-bg-primary/95 backdrop-blur-md border-b border-steel-dark"
          : "bg-transparent"
      }`}
    >
      {/* Orange top accent stripe */}
      <div className="h-[2px] w-full bg-signal" />
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          onClick={() => {
            // If already on home, force a smooth scroll to top since router
            // won't trigger any navigation/scroll for a same-route click.
            if (window.location.pathname === "/" && !window.location.hash) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else if (window.location.pathname === "/") {
              // hash present — clear it and scroll to top
              window.history.replaceState(null, "", "/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="flex items-center gap-3 group"
        >
          <EpicomLogo className="h-10 w-auto" />
        </Link>

        {/* Desktop nav */}
        {onHome && (
          <ul className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  data-testid={item.id}
                  className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal transition-colors"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="hidden lg:flex items-center gap-4">
          {!onHome && (
            <Link
              to="/"
              data-testid="nav-home-link"
              className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal transition-colors"
            >
              ← Epicom Systems
            </Link>
          )}
          <a
            href={onHome ? "#contact" : "/#contact"}
            data-testid="nav-cta-quote"
            className="bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 border-2 border-transparent hover:border-white/20 transition-all"
          >
            Request Quote
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className="lg:hidden p-2 text-white hover:text-signal"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-bg-primary border-t border-steel-dark"
        >
          <ul className="flex flex-col px-6 py-6 gap-5">
            {onHome &&
              navItems.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={() => setOpen(false)}
                    data-testid={`${item.id}-mobile`}
                    className="font-mono text-sm tracking-[0.2em] uppercase text-steel-light hover:text-signal"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            <li>
              <a
                href={onHome ? "#contact" : "/#contact"}
                onClick={() => setOpen(false)}
                data-testid="nav-cta-quote-mobile"
                className="inline-block bg-signal text-white font-bold uppercase tracking-wider text-sm px-6 py-3"
              >
                Request Quote
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
